/**
 * Phase 5Z.19l — Admin-only mass-select + delete helper for Einstellungen lists.
 *
 * Usage:
 *   const sel = useBulkSelect(items);
 *   // checkbox cells:
 *   <BulkSelectCheckbox sel={sel} id={item.id} testid={`xy-${item.id}`} />
 *   <BulkSelectAll items={items} sel={sel} testid="xy-all" />
 *   // floating bar:
 *   <BulkDeleteBar
 *     sel={sel}
 *     label="Abteilungen"
 *     deleteEndpoint={(id) => `/settings/departments/${id}`}
 *     onAfter={load}
 *   />
 *
 * Why loop single-delete instead of /admin/bulk-delete?
 * Because each settings list has its own business guards (default firm,
 * employee cascade, etc). Looping the existing single endpoint preserves
 * every guard while still feeling like a "mass" action to the user.
 */
import { useState, useMemo } from "react";
import { Trash2, X as XIcon, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

export function useBulkSelect(items, getId = (x) => x.id) {
  const [ids, setIds] = useState(() => new Set());
  return useMemo(() => ({
    has: (id) => ids.has(id),
    size: ids.size,
    list: () => Array.from(ids),
    toggle: (id) => setIds((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    }),
    clear: () => setIds(new Set()),
    setAll: (on) => setIds(on ? new Set((items || []).map(getId)) : new Set()),
    isAll: items && items.length > 0 && items.every((x) => ids.has(getId(x))),
    isSome: items && items.some((x) => ids.has(getId(x))),
  }), [ids, items, getId]);
}

export function isAdminUser(user) {
  if (!user) return false;
  return (user.role || "").toLowerCase() === "admin" || user.is_super_admin;
}

export function BulkSelectAll({ sel, testid = "bulk-select-all" }) {
  const { user } = useAuth();
  if (!isAdminUser(user)) return null;
  return (
    <input
      type="checkbox"
      title="Alle auswählen"
      checked={sel.isAll || false}
      onChange={(e) => sel.setAll(e.target.checked)}
      data-testid={testid}
      className="w-3.5 h-3.5 rounded accent-rose-500 cursor-pointer"
    />
  );
}

export function BulkSelectCheckbox({ sel, id, testid }) {
  const { user } = useAuth();
  if (!isAdminUser(user)) return null;
  return (
    <input
      type="checkbox"
      checked={sel.has(id)}
      onClick={(e) => e.stopPropagation()}
      onChange={() => sel.toggle(id)}
      data-testid={testid}
      className="w-3.5 h-3.5 rounded accent-rose-500 cursor-pointer"
    />
  );
}

/**
 * Floating mass-delete bar. Loops the existing single-delete endpoint so
 * each item goes through the same backend guards as a manual delete.
 */
export function BulkDeleteBar({ sel, label, deleteEndpoint, onAfter, testid = "bulk-delete-bar" }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!isAdminUser(user)) return null;
  if (sel.size === 0) return null;
  const count = sel.size;

  async function runDelete() {
    if (!window.confirm(`${count} ${label} wirklich löschen?\n\nDiese Aktion kann nicht rückgängig gemacht werden.`)) return;
    setBusy(true);
    let ok = 0;
    let failed = 0;
    let firstError = "";
    for (const id of sel.list()) {
      try {
        await api.delete(deleteEndpoint(id));
        ok += 1;
      } catch (e) {
        failed += 1;
        if (!firstError) firstError = e.response?.data?.detail || "Fehler";
      }
    }
    setBusy(false);
    sel.clear();
    onAfter?.();
    if (failed === 0) {
      toast.success(`${ok} ${label} gelöscht`);
    } else if (ok === 0) {
      toast.error(`Keine ${label} gelöscht. Erster Fehler: ${firstError}`);
    } else {
      toast.warning(`${ok} gelöscht · ${failed} fehlgeschlagen (${firstError})`);
    }
  }

  return (
    <div
      data-testid={testid}
      className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 bg-zinc-900/95 border border-rose-500/40 rounded-lg px-4 py-2.5 shadow-2xl backdrop-blur"
      role="toolbar"
      aria-label={`Massen-Löschen für ${label}`}
    >
      <div className="text-xs text-zinc-300 flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wider font-semibold text-rose-300">Admin</span>
        <span className="text-zinc-600">·</span>
        <span className="font-semibold text-zinc-100 tabular-nums">{count}</span>
        <span className="text-zinc-400">{label} ausgewählt</span>
      </div>
      <button
        type="button"
        onClick={runDelete}
        disabled={busy}
        data-testid={`${testid}-delete`}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white rounded transition-colors"
      >
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
        Löschen
      </button>
      <button
        type="button"
        onClick={sel.clear}
        disabled={busy}
        data-testid={`${testid}-clear`}
        title="Auswahl aufheben"
        className="p-1.5 text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800 rounded disabled:opacity-40"
      >
        <XIcon className="w-4 h-4" />
      </button>
    </div>
  );
}
